import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import ImageUploader from "@/components/ImageUploader";
import {
    MAKES,
    MODELS_BY_MAKE,
    PERFORMANCE_PACKAGES,
    ENGINES,
    DRIVETRAINS,
    TRANSMISSIONS,
    MODS_LEVELS,
    CURRENT_YEAR,
} from "@/lib/constants";

const empty = {
    year: CURRENT_YEAR,
    make: "Dodge",
    model_name: "Challenger",
    trim: "SRT Hellcat",
    engine: "6.2L Supercharged HEMI V8",
    drivetrain: "RWD",
    transmission: "Automatic",
    mods_level: "Stock",
    mods: [],
    photos: [],
    description: "",
    for_sale: false,
};

export default function EditVehicle() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [f, setF] = useState(empty);
    const [modInput, setModInput] = useState("");
    const [busy, setBusy] = useState(false);
    const isNew = !id;

    useEffect(() => {
        if (isNew) return;
        (async () => {
            try {
                const { data } = await api.get("/garage/vehicles");
                const veh = data.find((v) => v.vehicle_id === id);
                if (veh) setF({ ...empty, ...veh });
            } catch (e) {
                toast.error(formatApiError(e));
            }
        })();
    }, [id, isNew]);

    async function save(e) {
        e.preventDefault();
        setBusy(true);
        try {
            const payload = { ...f, year: Number(f.year) };
            if (isNew) {
                await api.post("/garage/vehicles", payload);
                toast.success("Vehicle added");
            } else {
                await api.patch(`/garage/vehicles/${id}`, payload);
                toast.success("Vehicle updated");
            }
            navigate("/garage");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    }

    async function remove() {
        if (!window.confirm("Delete this vehicle?")) return;
        try {
            await api.delete(`/garage/vehicles/${id}`);
            navigate("/garage");
        } catch (err) {
            toast.error(formatApiError(err));
        }
    }

    const models = MODELS_BY_MAKE[f.make] || [];

    return (
        <div className="min-h-screen bg-onyx-900">
            <div
                className="sticky top-0 z-30 backdrop-blur-xl border-b border-onyx-600"
                style={{ background: "rgba(10,10,10,0.85)" }}
            >
                <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-2">
                    <button
                        onClick={() => navigate(-1)}
                        className="btn-ghost rounded-sm px-2.5 py-2"
                        data-testid="back-btn"
                    >
                        <ArrowLeft className="w-4 h-4" />
                    </button>
                    <div className="headline text-lg">{isNew ? "Add Vehicle" : "Edit Vehicle"}</div>
                    {!isNew && (
                        <button
                            onClick={remove}
                            className="ml-auto btn-ghost rounded-sm px-3 py-2 text-xs font-bold uppercase tracking-widest inline-flex items-center gap-2 text-hellcat"
                            data-testid="delete-vehicle-btn"
                        >
                            <Trash2 className="w-4 h-4" /> Delete
                        </button>
                    )}
                </div>
            </div>

            <form onSubmit={save} className="max-w-3xl mx-auto px-4 py-5 space-y-4">
                <div className="surface rounded-md p-5">
                    <div className="headline text-lg mb-3">Photos</div>
                    <ImageUploader
                        value={f.photos}
                        onChange={(v) => setF({ ...f, photos: v })}
                        max={15}
                    />
                </div>

                <div className="surface rounded-md p-5 grid grid-cols-2 gap-3">
                    <Field label="Year">
                        <input
                            data-testid="vehicle-year"
                            type="number"
                            min={1970}
                            max={CURRENT_YEAR + 1}
                            required
                            value={f.year}
                            onChange={(e) => setF({ ...f, year: e.target.value })}
                            className={inputCls}
                        />
                    </Field>
                    <Field label="Make">
                        <select
                            data-testid="vehicle-make"
                            value={f.make}
                            onChange={(e) =>
                                setF({
                                    ...f,
                                    make: e.target.value,
                                    model_name: MODELS_BY_MAKE[e.target.value][0],
                                })
                            }
                            className={inputCls}
                        >
                            {MAKES.map((m) => (
                                <option key={m} value={m}>
                                    {m}
                                </option>
                            ))}
                        </select>
                    </Field>
                    <Field label="Model">
                        <select
                            data-testid="vehicle-model"
                            value={f.model_name}
                            onChange={(e) => setF({ ...f, model_name: e.target.value })}
                            className={inputCls}
                        >
                            {models.map((m) => (
                                <option key={m} value={m}>
                                    {m}
                                </option>
                            ))}
                        </select>
                    </Field>
                    <Field label="Trim">
                        <select
                            data-testid="vehicle-trim"
                            value={f.trim}
                            onChange={(e) => setF({ ...f, trim: e.target.value })}
                            className={inputCls}
                        >
                            {PERFORMANCE_PACKAGES.map((m) => (
                                <option key={m} value={m}>
                                    {m}
                                </option>
                            ))}
                        </select>
                    </Field>
                    <Field label="Engine">
                        <select
                            data-testid="vehicle-engine"
                            value={f.engine}
                            onChange={(e) => setF({ ...f, engine: e.target.value })}
                            className={inputCls}
                        >
                            {ENGINES.map((m) => (
                                <option key={m} value={m}>
                                    {m}
                                </option>
                            ))}
                        </select>
                    </Field>
                    <Field label="Drivetrain">
                        <select
                            data-testid="vehicle-drivetrain"
                            value={f.drivetrain}
                            onChange={(e) => setF({ ...f, drivetrain: e.target.value })}
                            className={inputCls}
                        >
                            {DRIVETRAINS.map((m) => (
                                <option key={m} value={m}>
                                    {m}
                                </option>
                            ))}
                        </select>
                    </Field>
                    <Field label="Transmission">
                        <select
                            data-testid="vehicle-transmission"
                            value={f.transmission}
                            onChange={(e) => setF({ ...f, transmission: e.target.value })}
                            className={inputCls}
                        >
                            {TRANSMISSIONS.map((m) => (
                                <option key={m} value={m}>
                                    {m}
                                </option>
                            ))}
                        </select>
                    </Field>
                    <Field label="Mods level">
                        <select
                            data-testid="vehicle-mods-level"
                            value={f.mods_level}
                            onChange={(e) => setF({ ...f, mods_level: e.target.value })}
                            className={inputCls}
                        >
                            {MODS_LEVELS.map((m) => (
                                <option key={m} value={m}>
                                    {m}
                                </option>
                            ))}
                        </select>
                    </Field>
                </div>

                <div className="surface rounded-md p-5">
                    <div className="headline text-lg mb-3">Mods</div>
                    <div className="flex gap-2 flex-wrap mb-2">
                        {f.mods.map((m, i) => (
                            <span
                                key={i}
                                className="surface-elevated rounded-sm px-3 py-1 text-sm inline-flex items-center gap-2"
                            >
                                {m}
                                <button
                                    type="button"
                                    onClick={() =>
                                        setF({
                                            ...f,
                                            mods: f.mods.filter((_, idx) => idx !== i),
                                        })
                                    }
                                    className="text-hellcat"
                                >
                                    ×
                                </button>
                            </span>
                        ))}
                    </div>
                    <div className="flex gap-2">
                        <input
                            data-testid="vehicle-mod-input"
                            value={modInput}
                            onChange={(e) => setModInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                    if (modInput.trim()) {
                                        setF({ ...f, mods: [...f.mods, modInput.trim()] });
                                        setModInput("");
                                    }
                                }
                            }}
                            className={`${inputCls} flex-1`}
                            placeholder="e.g. Custom tune"
                        />
                        <button
                            type="button"
                            onClick={() => {
                                if (modInput.trim()) {
                                    setF({ ...f, mods: [...f.mods, modInput.trim()] });
                                    setModInput("");
                                }
                            }}
                            className="btn-ghost rounded-sm px-4 text-sm font-bold uppercase tracking-widest"
                            data-testid="vehicle-add-mod"
                        >
                            Add
                        </button>
                    </div>
                </div>

                <div className="surface rounded-md p-5">
                    <div className="headline text-lg mb-3">Notes</div>
                    <textarea
                        data-testid="vehicle-description"
                        value={f.description}
                        onChange={(e) => setF({ ...f, description: e.target.value })}
                        className={`${inputCls} h-24`}
                        placeholder="Anything you want to share about this build..."
                    />
                    <label className="flex items-center gap-2 mt-3 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={!!f.for_sale}
                            onChange={(e) => setF({ ...f, for_sale: e.target.checked })}
                            data-testid="vehicle-for-sale"
                            className="w-4 h-4 accent-hellcat"
                        />
                        <span className="text-sm">Marked as For Sale</span>
                    </label>
                </div>

                <button
                    type="submit"
                    disabled={busy}
                    data-testid="vehicle-submit"
                    className="btn-red w-full py-3.5 rounded-sm disabled:opacity-60"
                >
                    {busy ? "Saving..." : "Save vehicle"}
                </button>
            </form>
        </div>
    );
}

const inputCls =
    "w-full bg-onyx-700 border border-onyx-600 rounded-sm px-3 py-2.5 text-sm text-white focus:outline-none focus:border-hellcat";

function Field({ label, children, className = "" }) {
    return (
        <label className={`flex flex-col gap-1.5 ${className}`}>
            <span className="label-caps">{label}</span>
            {children}
        </label>
    );
}
